import React from 'react'

import {
  AverageSpendComparisonIcon,
  BeatCategoryIcon,
  BeatMaterialIcon,
  BeatMerchantLogo,
  MonthlySpendComparisonIcon,
  AreaChartIcon,
  InstitutionLogoIcon,
} from '.'

import type { WeGotTheBeat } from '../interfaces'
import type { Theme } from '@mui/material/styles'
import { alpha, useTheme } from '@mui/material/styles'

const iconTypeMap: Record<number, (beat: WeGotTheBeat['beat'], theme: Theme) => React.JSX.Element> =
  {
    0: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="error" />
    ),
    1: (beat) => <BeatCategoryIcon beat={beat} />,
    2: (beat) => <BeatMerchantLogo beat={beat} />,
    3: (beat) => <MonthlySpendComparisonIcon beat={beat} />,
    4: (_, theme) => {
      return (
        <BeatMaterialIcon
          bgColor={alpha(theme.palette.secondary.main, 0.15)}
          iconColor="secondary"
          name="info"
        />
      )
    },
    5: (_, theme) => {
      return <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="campaign" />
    },
    6: (_, theme) => {
      return (
        <BeatMaterialIcon
          bgColor={alpha(theme.palette.success.main, 0.15)}
          iconColor="success"
          name="check_circle"
        />
      )
    },
    7: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="today" />
    ),
    8: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="calendar_clock" />
    ),
    9: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="celebration" />
    ),
    // TODO: Waiting for the backend to accommodate the below icon types
    10: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="problem" />
    ),
    11: (_, theme) => (
      <BeatMaterialIcon
        avatarSx={{ border: `1px solid ${theme.palette.secondary.main}` }}
        bgColor={alpha(theme.palette.secondary.main, 0.15)}
        fill={false}
        name="category"
        sx={{ color: 'text.primary' }}
      />
    ),
    12: (beat) => <AreaChartIcon beat={beat} />,
    13: () => (
      <BeatMaterialIcon
        avatarSx={{ border: '1px solid #EB00D0' }} //TODO : Remove explict color code
        bgColor={alpha('#EB00D0', 0.25)}
        fill={false}
        name="money_bag"
        sx={{ color: 'text.primary' }}
      />
    ),
    14: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.secondary.main, 0.15)}
        fill={false}
        iconColor="secondary"
        name="person"
      />
    ),
    15: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.primary.main, 0.15)}
        name="payment_arrow_down"
      />
    ),
    16: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.success.main, 0.15)}
        name="beenhere"
        sx={{ color: 'success.main' }}
      />
    ),
    17: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="savings" />
    ),
    18: (beat, theme) => (
      <BeatMaterialIcon
        bgColor={
          beat.payload.projected_overdraft_amount < 0
            ? alpha(theme.palette.warning.main, 0.15)
            : alpha(theme.palette.error.main, 0.15)
        }
        iconColor={beat?.payload?.projected_overdraft_amount < 0 ? 'warning' : 'error'}
        name="error"
      />
    ),
    19: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="finance_mode" />
    ),
    20: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="tips_and_updates" />
    ),
    21: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.primary.main, 0.15)}
        name="currency_exchange"
      />
    ),
    22: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.warning.main, 0.15)}
        name="flag"
        sx={{ color: 'warning.main' }}
      />
    ),
    23: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="thumb_up" />
    ),
    24: () => (
      <BeatMaterialIcon
        bgColor={alpha('#EB00D0', 0.15)} //TODO : Remove explict color code
        name="attach_money"
        sx={{ color: '#EB00D0' }}
      />
    ),
    25: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="note_stack" />
    ),
    26: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.primary.main, 0.15)}
        fill={false}
        name="price_change"
      />
    ),
    27: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="directions_car" />
    ),
    28: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="contract" />
    ),
    29: (beat) => <AverageSpendComparisonIcon beat={beat} />,
    30: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="calendar_month" />
    ),
    31: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.warning.main, 0.15)}
        name="star_rate"
        sx={{ color: 'warning.400' }}
      />
    ),
    32: (_, theme) => (
      <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="help" />
    ),
    33: (_, theme) => (
      <BeatMaterialIcon
        bgColor={alpha(theme.palette.primary.main, 0.15)}
        fill={false}
        name="storefront"
      />
    ),
    34: (beat) => <InstitutionLogoIcon beat={beat} />,
  }

export const BeatIcon: React.FC<WeGotTheBeat> = ({ beat }) => {
  const theme = useTheme()
  const { icon_type } = beat
  const renderIcon = icon_type != null ? iconTypeMap[icon_type] : null

  if (renderIcon) {
    return renderIcon(beat, theme)
  }

  return <BeatMaterialIcon bgColor={alpha(theme.palette.primary.main, 0.15)} name="error" />
}
